import { TextareaInput } from 'components/FormControl';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Toast } from 'primereact/toast';

import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { formatFileNameProduct } from 'utils/func';
import CustomFileInput from 'components/FormControl/CustomFileInput';
import { addSoftProductsSchema } from 'features/Project/validation';
import { yupResolver } from '@hookform/resolvers/yup';

import { productDefaultValues } from 'features/Project/defaultValues';
import { PRODUCT_TYPE } from 'constant/summary-report.constant';

const SoftProductDialog = forwardRef(({ handleDownloadFile, productTypeShort }, ref) => {
  // #region Data
  const { t } = useTranslation();
  const toast = useRef(null);

  const [productControl, setSoftProductControl] = useState();
  const [visible, setVisible] = useState(false);

  const {
    control,
    getValues,
    reset,

    trigger,
    formState: { errors, dirtyFields },
  } = useForm({
    mode: 'onChange',
    resolver: yupResolver(addSoftProductsSchema),
    defaultValues: productDefaultValues,
  });

  // #endregion Data

  // #region Event
  const showError = (message) => {
    toast.current.show({
      severity: 'error',
      summary: t('toast.error'),
      detail: message,
      life: 4000,
    });
  };

  useImperativeHandle(
    ref,
    () => ({
      open: (_productControl) => {
        setSoftProductControl(_productControl);
        setVisible(true);
        if (_productControl.type === 'INSERT') {
          reset({});
        } else if (_productControl.type === 'UPDATE') {
          reset(_productControl.value);
        }
      },
    }),
    []
  );

  const handleAddProduct = async () => {
    const isValidTrigger = await trigger();
    if (!isValidTrigger) {
      showError(t('errorMessage.validationErrorMessage'));
      return;
    }

    const data = getValues();

    const { insert, update, index, type } = productControl;

    if (type === 'INSERT' && insert) {
      insert(index, data);
    } else if (type === 'UPDATE' && update) {
      update(index, { ...data, isUpdateNewValue: true });
    }
    reset({});
    setVisible(false);
  };

  // #endregion Event

  return (
    <>
      <Dialog
        header={
          productControl?.type === 'INSERT'
            ? t('submitDocument.productDialog.insert.title')
            : t('submitDocument.productDialog.update.title')
        }
        visible={visible}
        onHide={() => {
          setVisible(false);
        }}
        draggable={false}
        className="w-full sm:w-7 md:w-6 lg:w-5"
      >
        <div className="p-fluid">
          <div className="grid">
            <div className="col-12">
              <TextareaInput
                name="productDetail.name"
                label={t('submitDocument.product.productDetail.name')}
                isRequired
                control={control}
                errors={errors}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <TextareaInput
                name="productDetail.evaluativeCriteria"
                label={t('submitDocument.product.productDetail.evaluationCriteria')}
                isRequired
                control={control}
                errors={errors}
              />
            </div>
          </div>

          <div className="grid">
            <div className="col-12">
              <TextareaInput
                name="productDetail.note"
                label={t('submitDocument.product.productDetail.note')}
                control={control}
                errors={errors}
                row={3}
              />
            </div>
          </div>
          <div className="grid">
            <div className="col-12">
              <CustomFileInput
                name="file"
                label={t('submitDocument.product.evidenceFile')}
                defaultNameFile={`${formatFileNameProduct(
                  'a.pdf',
                  productControl?.projectCode,
                  productTypeShort,
                  !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                )}`}
                isRequired
                control={control}
                errors={errors}
                accept=".pdf"
                maxFileSize={30000000}
              />
              {productControl?.value?.fileUrl ? (
                <span
                  className="p-button p-button-text p-button-sm mt-1 p-0"
                  onClick={
                    () =>
                      handleDownloadFile(
                        productControl?.value?.fileUrl,
                        formatFileNameProduct(
                          productControl?.value?.fileUrl || '',
                          productControl?.projectCode,
                          productTypeShort,
                          !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                        )
                      )
                    // eslint-disable-next-line react/jsx-curly-newline
                  }
                >
                  <i className="pi pi-file-pdf text-red-500 text-xl" />{' '}
                  <span className="text-blue-500 underline font-italic ml-1">
                    {formatFileNameProduct(
                      productControl?.value?.fileUrl || '',
                      productControl?.projectCode,
                      productTypeShort,
                      !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                    )}
                  </span>
                </span>
              ) : (
                <span>
                  {productControl?.value?.file?.name &&
                    formatFileNameProduct(
                      productControl?.value?.file?.name || '',
                      productControl?.projectCode,
                      productTypeShort,
                      !productControl?.index ? 1 : parseInt(productControl?.index, 10) + 1
                    )}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-content-end mt-4">
          <Button
            label={
              productControl?.type === 'INSERT'
                ? t('formLayout.action.add')
                : t('formLayout.action.update')
            }
            type="submit"
            severity="info"
            onClick={handleAddProduct}
            className="w-8rem"
            disabled={!Object.keys(dirtyFields)?.length}
          />
        </div>
      </Dialog>
      <Toast ref={toast} />
    </>
  );
});

SoftProductDialog.propTypes = {
  handleDownloadFile: PropTypes.func.isRequired,
  productTypeShort: PropTypes.string,
};

SoftProductDialog.defaultProps = {
  productTypeShort: PRODUCT_TYPE.SOFT_PRODUCT.short,
};

export default SoftProductDialog;
